/**
 * Tooltip + InfoHint — move explanatory text off the canvas and onto demand.
 *
 * Tooltip wraps any focusable element and shows `text` on hover/focus. The bubble is
 * `position: fixed` (computed from the trigger rect) AND rendered through a portal onto
 * `document.body`, so it escapes overflow/clip containers — the same reason the design system
 * forbids absolute dropdowns inside overflow:auto. InfoHint is the standalone ⓘ button for
 * "more info" next to a heading or label.
 */
import { cloneElement, isValidElement, type ReactNode, type RefObject, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon.js";

/** The gap between the trigger's edge and the bubble, and the viewport gutter. */
const GAP = 8;
/** `.tt__bubble`'s `max-width`, used to clamp before the bubble has measured itself. */
const MAX_W = 260;

/**
 * The tooltip MECHANISM, without the wrapper element.
 *
 * `<Tooltip>` wraps its child in a `<span class="tt">`, which is fine almost everywhere and
 * illegal inside a `role="tablist"`: a tablist's element children must all be `role="tab"`,
 * and wrapping each tab in a span is the first of AUD-2 IM-7's three defects. `ui/Tabs.tsx`
 * therefore attaches these handlers to the tab button itself.
 *
 * `bubble` is a PORTAL, not a sibling. It used to be a plain element, which made its mount
 * point whatever the caller's tree happened to be — and in `ui/Tabs.tsx` that is the inside of
 * the tablist, so the group carried a non-tab child for the whole time a tab was hovered or
 * focused. A portal takes the decision away from the caller: wherever `{bubble}` is written,
 * the `role="tooltip"` element lands on `document.body`, tied to its trigger by
 * `aria-describedby` and by a position read from the trigger's own rect.
 *
 * One mechanism, three mountings — not a second tooltip implementation.
 */
export function useTooltipTrigger<T extends HTMLElement = HTMLButtonElement>(
  text: string | undefined,
  placement: "bottom" | "top" = "bottom",
) {
  const [open, setOpen] = useState(false);
  /** `side` is the placement ACTUALLY used — `placement` flipped if it had no room. */
  const [pos, setPos] = useState<{ top: number; left: number; side: "bottom" | "top" }>({
    top: 0,
    left: 0,
    side: placement,
  });
  const ref = useRef<T>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  /* Anchored to the trigger, flipped at the viewport edge. The bubble's own rect is used once
   * it exists (the effect below re-places after mount); before that the CSS max-width and a
   * one-line height stand in, which is what the pre-portal code assumed outright. */
  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const b = bubbleRef.current?.getBoundingClientRect();
    const bw = b?.width || MAX_W;
    const bh = b?.height || 36;
    const below = window.innerHeight - r.bottom - GAP;
    const above = r.top - GAP;
    let side = placement;
    if (side === "bottom" && below < bh && above > below) side = "top";
    else if (side === "top" && above < bh && below > above) side = "bottom";
    setPos({
      top: side === "top" ? r.top - GAP : r.bottom + GAP,
      left: Math.max(GAP, Math.min(r.left, window.innerWidth - bw - GAP)),
      side,
    });
  }, [placement]);

  const show = () => {
    if (!ref.current || !text) return;
    place();
    setOpen(true);
  };
  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    // Re-place now that the bubble is in the document and can be measured.
    place();
    const onMove = () => place();
    const onKey = (e: KeyboardEvent) => {
      // Escape dismisses the bubble and nothing else: focus stays on the trigger, which is
      // where the user put it. Re-opening is one blur/focus or one pointer move away.
      if (e.key === "Escape") setOpen(false);
    };
    // Capture, so a scroll inside any ancestor pane moves the bubble with its trigger.
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  return {
    ref: ref as RefObject<T | null>,
    triggerProps: {
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
      "aria-describedby": open && text ? id : undefined,
    },
    bubble:
      open && text
        ? createPortal(
            <span
              ref={bubbleRef}
              role="tooltip"
              id={id}
              className={`tt__bubble tt__bubble--${pos.side}`}
              style={{ position: "fixed", top: pos.top, left: pos.left }}
            >
              {text}
            </span>,
            document.body,
          )
        : null,
  };
}

export function Tooltip({
  text,
  children,
  placement = "bottom",
}: {
  text: string;
  children: ReactNode;
  placement?: "bottom" | "top";
}) {
  const tip = useTooltipTrigger<HTMLSpanElement>(text, placement);
  /* `aria-describedby` has to sit on the element that TAKES FOCUS — the `<button>` inside —
     or a screen reader on the button never hears the text; the wrapper is not in the tab
     order. When the child is one element it is cloned with the id; the wrapper keeps it too
     for a non-element child (plain text) that cannot carry attributes. */
  const described = tip.triggerProps["aria-describedby"];
  const child =
    isValidElement<{ "aria-describedby"?: string }>(children) && described
      ? cloneElement(children, { "aria-describedby": described })
      : children;
  return (
    <span className="tt" ref={tip.ref} {...tip.triggerProps}>
      {child}
      {tip.bubble}
    </span>
  );
}

export function InfoHint({ text, label = "More info" }: { text: string; label?: string }) {
  return (
    <Tooltip text={text}>
      <button type="button" className="info-hint" aria-label={label}>
        <Icon name="info" size={16} />
      </button>
    </Tooltip>
  );
}
